import {Tooltip} from 'platform/components';
import {Clickable, Image} from 'platform/foundation';

import {useState} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {parseDate, useTimeout} from 'shared/web';

import {useOpenSourcingCebiaReport} from '../../../hooks/useOpenSourcingCebiaReport';
import {isSourcingCebiaReportUrlExpired} from '../../../utils/isSourcingCebiaReportUrlExpired';
import {CEBIA_BRAND} from '../constants/CEBIA_BRAND';
import {useSourcingCebiaReport} from '../hooks/useSourcingCebiaReport';

interface SourcingCebiaReportBadgeProps {
  externalId: string;
}

const MAX_TIMEOUT_DELAY_MS = 2_147_483_647;

function getExpiryDelay(expiresAt: number | null, isExpired: boolean): number | null {
  if (expiresAt === null || !Number.isFinite(expiresAt) || isExpired) {
    return null;
  }
  return Math.min(Math.max(expiresAt - Date.now() + 1, 0), MAX_TIMEOUT_DELAY_MS);
}

export function SourcingCebiaReportBadge(props: SourcingCebiaReportBadgeProps) {
  const {latestCompletedReportId, latestCompletedVin, latestCompletedReportUrlValidTo} =
    useSourcingCebiaReport(props.externalId);
  const {openReport, isOpening} = useOpenSourcingCebiaReport(props.externalId);
  const [, setExpiryTick] = useState(0);
  const expiresAt = isNotNilOrEmpty(latestCompletedReportUrlValidTo)
    ? (parseDate(latestCompletedReportUrlValidTo)?.getTime() ?? null)
    : null;
  const isExpired = isSourcingCebiaReportUrlExpired(latestCompletedReportUrlValidTo);
  const delay = getExpiryDelay(expiresAt, isExpired);
  useTimeout(() => setExpiryTick((tick) => tick + 1), delay);

  if (!isNotNilOrEmpty(latestCompletedReportId)) {
    return null;
  }

  const statusText = isExpired
    ? i18n.t('entity.cebiaReport.urlExpired')
    : i18n.t('page.integrations.labels.cebiaReportViewResults');
  const label = `${CEBIA_BRAND} · ${statusText}`;
  const fullLabel = latestCompletedVin
    ? `${label} · ${i18n.t('entity.vehicle.labels.vin')}: ${latestCompletedVin}`
    : label;

  return (
    <Tooltip label={fullLabel}>
      <Clickable
        role="button"
        tabIndex={0}
        aria-label={fullLabel}
        isDisabled={isOpening}
        width={5}
        height={5}
        onClick={(event) => {
          event.stopPropagation();
          if (!isOpening) {
            openReport();
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            if (!isOpening) {
              openReport();
            }
          }
        }}
      >
        <Image src="/assets/images/cebia-autotracer-icon.png" alt="" width={5} height={5} />
      </Clickable>
    </Tooltip>
  );
}
